import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { BRAND_MARK_PATHS, BRAND_MARK_VIEWBOX } from './brandMark';
import { Layout, Menu, Dropdown, Modal, Form, Input, message } from 'antd';
import {
  LogoutOutlined,
  UserOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  SettingOutlined,
  BellOutlined,
  MoonOutlined,
  SunOutlined,
} from '@ant-design/icons';
import { clearAuth, getUser } from '../../utils/auth';
import { authApi } from '../../api/auth';
import { L } from '../../styles/theme';
import { useThemeMode } from '../../styles/ThemeMode';

const { Sider, Content } = Layout;

/** Visible to: admin only. Operators / moderators do not see these. */
const ADMIN_ONLY_KEYS = new Set<string>([
  '/users',
  '/staff',
  '/analytics',
  '/push',
  '/districts-map',
  '/districts',
  '/streets',
  '/data-sync',
  '/cinema',
  '/assistant',
]);

type Role = 'admin' | 'operator' | 'moderator' | string;

const PAGE_TITLES: Record<string, string> = {
  '/dashboard': 'Дашборд',
  '/news': 'Новости',
  '/events': 'Афиша',
  '/holidays': 'Праздники',
  '/delivery-promos': 'Акции доставки',
  '/polls': 'Опросы',
  '/push': 'Push-центр',
  '/chat': 'Городской чат',
  '/appeals': 'Обращения',
  '/appointments': 'Запись к акиму',
  '/shutoffs': 'Отключения',
  '/city-notices': 'Городские уведомления',
  '/akimat-directory': 'Акимат',
  '/akimat-documents': 'Документы акимата',
  '/heating': 'Отопительный сезон',
  '/directory': 'Справочник',
  '/brand-logos': 'Логотипы',
  '/classifieds': 'Объявления',
  '/jobs': 'Вакансии',
  '/currency': 'Курсы валют',
  '/cinema': 'Кинотеатр',
  '/transport': 'Транспорт',
  '/ecology': 'Экология',
  '/village': 'Бурлинский район',
  '/districts': 'Районы',
  '/districts-map': 'Районы на карте',
  '/streets': 'Улицы',
  '/users': 'Жители',
  '/staff': 'Сотрудники',
  '/analytics': 'Аналитика',
  '/city-objects': 'Городские объекты',
  '/data-sync': 'Данные 2ГИС',
  '/assistant': 'Разбор Айланы',
};

function buildMenuItems(role: Role) {
  const isAdmin = role === 'admin';
  const filterAdminOnly = (children: { key?: string; children?: any[] }[]) =>
    children
      .map((c) => {
        if (c.children) {
          const subs = c.children.filter((s: any) => isAdmin || !ADMIN_ONLY_KEYS.has(s.key));
          return subs.length === 0 ? null : { ...c, children: subs };
        }
        return c.key && !isAdmin && ADMIN_ONLY_KEYS.has(c.key) ? null : c;
      })
      .filter(Boolean) as any[];

  const groups = [
    {
      type: 'group' as const,
      label: 'Контент',
      children: [
        { key: '/news', label: 'Новости' },
        { key: '/events', label: 'Афиша' },
        { key: '/holidays', label: 'Праздники' },
        { key: '/delivery-promos', label: 'Акции доставки' },
        { key: '/polls', label: 'Опросы' },
        { key: '/push', label: 'Push-центр' },
        { key: '/chat', label: 'Городской чат' },
      ],
    },
    {
      type: 'group' as const,
      label: 'Госуслуги',
      children: [
        { key: '/appeals', label: 'Обращения' },
        { key: '/appointments', label: 'Запись к акиму' },
        { key: '/shutoffs', label: 'Отключения' },
        { key: '/city-notices', label: 'Городские уведомления' },
        { key: '/akimat-directory', label: 'Акимат' },
        { key: '/akimat-documents', label: 'Документы акимата' },
        { key: '/heating', label: 'Отопительный сезон' },
      ],
    },
    {
      type: 'group' as const,
      label: 'Город',
      children: [
        { key: '/directory', label: 'Справочник' },
        { key: '/brand-logos', label: 'Логотипы' },
        { key: '/classifieds', label: 'Объявления' },
        { key: '/jobs', label: 'Вакансии' },
        { key: '/currency', label: 'Курсы валют' },
        { key: '/cinema', label: 'Кинотеатр' },
      ],
    },
    {
      type: 'group' as const,
      label: 'Инфраструктура',
      children: [
        { key: '/transport', label: 'Транспорт' },
        { key: '/ecology', label: 'Экология' },
      ],
    },
    {
      type: 'group' as const,
      label: 'Район',
      children: [
        { key: '/village', label: 'Бурлинский район' },
        {
          key: 'geo',
          label: 'Геоданные',
          children: [
            { key: '/districts', label: 'Районы' },
            { key: '/districts-map', label: 'Районы на карте' },
            { key: '/streets', label: 'Улицы' },
            { key: '/city-objects', label: 'Городские объекты' },
            { key: '/data-sync', label: 'Данные 2ГИС' },
          ],
        },
      ],
    },
    {
      type: 'group' as const,
      label: 'Система',
      children: [
        { key: '/users', label: 'Жители' },
        { key: '/staff', label: 'Сотрудники' },
        { key: '/analytics', label: 'Аналитика' },
        { key: '/assistant', label: 'Разбор Айланы' },
      ],
    },
  ];

  return [
    { key: '/dashboard', label: 'Дашборд' },
    ...groups
      .map((g) => ({ ...g, children: filterAdminOnly(g.children as any[]) }))
      .filter((g) => (g.children as any[]).length > 0),
  ];
}

const AdminLayout: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const user = getUser();
  const { mode, toggle } = useThemeMode();
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [passwordForm] = Form.useForm();

  const handleChangePassword = async () => {
    let values: { current_password: string; new_password: string; repeat: string };
    try { values = await passwordForm.validateFields(); } catch { return; }
    setSavingPassword(true);
    try {
      await authApi.changePassword(values.current_password, values.new_password);
      message.success('Пароль изменён');
      setPasswordOpen(false);
      passwordForm.resetFields();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Не удалось изменить пароль');
    } finally {
      setSavingPassword(false);
    }
  };

  const handleLogout = () => {
    clearAuth();
    navigate('/login');
  };

  const userMenu = {
    items: [
      {
        key: 'profile',
        icon: <UserOutlined />,
        label: user?.full_name || user?.phone || 'Профиль',
      },
      {
        key: 'password',
        icon: <SettingOutlined />,
        label: 'Сменить пароль',
        onClick: () => setPasswordOpen(true),
      },
      { type: 'divider' as const },
      {
        key: 'logout',
        icon: <LogoutOutlined />,
        label: 'Выйти',
        danger: true,
        onClick: handleLogout,
      },
    ],
  };

  const pageTitle =
    PAGE_TITLES[location.pathname] ||
    PAGE_TITLES['/' + location.pathname.split('/')[1]] ||
    'Ақсайым';

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        width={216}
        collapsedWidth={0}
        style={{
          background: L.side,
          borderRight: `1px solid ${L.line}`,
          overflow: 'auto',
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          zIndex: 100,
        }}
        theme="light"
      >
        <div
          style={{
            height: 52,
            display: 'flex',
            alignItems: 'center',
            padding: '0 16px',
            borderBottom: `1px solid ${L.line}`,
            position: 'sticky',
            top: 0,
            background: L.side,
            zIndex: 1,
          }}
        >
          <div
            style={{
              width: 24,
              height: 24,
              borderRadius: 7,
              background: L.accent,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <svg viewBox={BRAND_MARK_VIEWBOX} width={12} height={15} fill="#fff" aria-label="Ақсайым">
              {BRAND_MARK_PATHS.map((d) => (
                <path key={d.slice(0, 16)} d={d} fillRule="evenodd" />
              ))}
            </svg>
          </div>
          <div style={{ marginLeft: 9 }}>
            <div style={{ color: L.ink, fontWeight: 600, fontSize: 13, lineHeight: 1.2, letterSpacing: '-0.01em' }}>
              Ақсайым
            </div>
            <div style={{ color: L.muted, fontSize: 10 }}>панель управления</div>
          </div>
        </div>

        <Menu
          mode="inline"
          selectedKeys={[location.pathname]}
          items={buildMenuItems(user?.role || 'operator')}
          onClick={({ key }) => navigate(key)}
          style={{
            background: 'transparent',
            borderRight: 'none',
            padding: '8px 0 24px',
          }}
        />
      </Sider>

      <Layout style={{ marginLeft: collapsed ? 0 : 216, transition: 'margin-left 0.2s', background: L.bg }}>
        <div
          style={{
            background: L.surface,
            padding: '0 20px',
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            borderBottom: `1px solid ${L.line}`,
            height: 46,
            position: 'sticky',
            top: 0,
            zIndex: 99,
          }}
        >
          <div
            onClick={() => setCollapsed(!collapsed)}
            style={{ cursor: 'pointer', fontSize: 14, color: L.muted, display: 'flex', alignItems: 'center' }}
          >
            {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          </div>
          <span style={{ fontSize: 12.5, color: L.muted }}>
            Ақсайым&nbsp;/&nbsp;
            <span style={{ color: L.ink, fontWeight: 500 }}>{pageTitle}</span>
          </span>

          <span style={{ flex: 1 }} />

          <span
            onClick={toggle}
            title={mode === 'dark' ? 'Светлая тема' : 'Тёмная тема'}
            style={{ fontSize: 15, color: L.muted, cursor: 'pointer', display: 'flex' }}
          >
            {mode === 'dark' ? <SunOutlined /> : <MoonOutlined />}
          </span>
          <BellOutlined style={{ fontSize: 15, color: L.muted, cursor: 'pointer' }} />
          <Dropdown menu={userMenu} placement="bottomRight" arrow>
            <span style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
              <span
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: '50%',
                  background: L.accent,
                  color: '#fff',
                  fontSize: 11,
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {(user?.full_name || 'А').slice(0, 1).toUpperCase()}
              </span>
              <span style={{ lineHeight: 1.25 }}>
                <span style={{ fontSize: 12, color: L.text, display: 'block' }}>
                  {user?.full_name || 'Администратор'}
                </span>
                <span style={{ fontSize: 10, color: L.faint, display: 'block' }}>
                  {user?.role === 'admin' ? 'администратор' : user?.role || 'оператор'}
                </span>
              </span>
            </span>
          </Dropdown>
        </div>

        <Content style={{ margin: '20px 24px', minHeight: 280 }}>
          <Outlet />
        </Content>

        <Modal
          title="Смена пароля"
          open={passwordOpen}
          onCancel={() => setPasswordOpen(false)}
          onOk={handleChangePassword}
          confirmLoading={savingPassword}
          okText="Сохранить"
          cancelText="Отмена"
          width={400}
          destroyOnClose
        >
          <Form form={passwordForm} layout="vertical" style={{ marginTop: 14 }}>
            <Form.Item
              name="current_password"
              label="Текущий пароль"
              rules={[{ required: true, message: 'Введите текущий пароль' }]}
            >
              <Input.Password autoComplete="current-password" />
            </Form.Item>
            <Form.Item
              name="new_password"
              label="Новый пароль"
              rules={[{ required: true, min: 8, message: 'Не короче 8 символов' }]}
            >
              <Input.Password autoComplete="new-password" />
            </Form.Item>
            <Form.Item
              name="repeat"
              label="Повторите новый пароль"
              dependencies={['new_password']}
              rules={[
                { required: true, message: 'Повторите пароль' },
                ({ getFieldValue }) => ({
                  validator: (_, v) =>
                    !v || getFieldValue('new_password') === v
                      ? Promise.resolve()
                      : Promise.reject(new Error('Пароли не совпадают')),
                }),
              ]}
            >
              <Input.Password autoComplete="new-password" />
            </Form.Item>
          </Form>
        </Modal>
      </Layout>
    </Layout>
  );
};

export default AdminLayout;
